"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-full text-sm font-semibold transition-[color,background-color,border-color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--control-focus,#18181b)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px active:scale-[0.99] cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-[var(--button-primary-bg,#18181b)] text-[var(--button-primary-text,#fff)] hover:bg-[var(--button-primary-bg-hover,#27272a)]",
        destructive: "bg-red-600 text-white hover:bg-red-700",
        outline: "border border-[var(--button-border,#d4d4d8)] bg-transparent text-[var(--button-secondary-text,#18181b)] hover:bg-[var(--button-secondary-bg,#f4f4f5)]",
        secondary: "bg-[var(--button-secondary-bg,#f4f4f5)] text-[var(--button-secondary-text,#18181b)] hover:bg-[var(--button-secondary-bg-hover,#e4e4e7)]",
        ghost: "text-[var(--button-secondary-text,#18181b)] hover:bg-[var(--button-secondary-bg,#f4f4f5)]",
        link: "text-[var(--button-secondary-text,#18181b)] underline-offset-4 hover:underline",
        higher: "bg-emerald-700 text-white hover:bg-emerald-800 shadow-lg shadow-emerald-700/20",
        lower: "bg-rose-700 text-white hover:bg-rose-800 shadow-lg shadow-rose-700/20"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-10 px-4",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
